import { useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';

import { SitePickerBar } from '/src/components/site-picker/index';
import { SEO } from '/src/components/SEO';
import { DashboardPaywall } from '../../_global/paywalls/DashboardPaywall';
import { DashboardPage } from '/src/components/Page';
import { SelectionProvider } from '/src/contexts/selection';
import { FilterProvider } from '../snapshots-filter-context';
import { SnapshotActionProvider } from '../snapshot-actions-context';
import { InstallationBanner } from '/src/components/banners/InstallationBanner';
import { useSite, usePermissions, useModal } from '/src/hooks';
import { getFeatureName } from '../../_global/utils';
import { FEATURES } from '/src/features/_global/constants';
import { SnapshotAlerts } from '/src/features/_global/components/SnapshotAlerts';

import { SnapshotDashboardContent } from '/src/features/snapshots/components/dashboard/SnapshotDashboardContent';
import { SnapshotDashHeader } from '/src/features/snapshots/components/dashboard/SnapshotDashHeader';
import { OnboardingHeatmapsCreatedModal } from '/src/features/snapshots/components/dashboard/modals/OnboardingHeatmapsCreatedModal';

import { SNAPSHOTS_SITES_QUERY } from '/src/features/snapshots/queries';
import { SourceSelector } from '/src/components/source-selector/source-selector';

export function SnapshotDashboard() {
  const [searchParams, setSearchParams] = useSearchParams();
  const modal = useModal();
  const hasShownOnboardingModalRef = useRef(false);

  const permissions = usePermissions();
  const { allowed: canViewDashboard, reason } = permissions.can('view', FEATURES.SNAPSHOTS);
  const canCreate = permissions.can('create', FEATURES.SNAPSHOTS).allowed;
  const canEditSites = permissions.can('create', FEATURES.SITES).allowed;

  const { sites, loadingSites } = useSite({
    sitesQuery: SNAPSHOTS_SITES_QUERY,
    enableAllSites: true,
    sitesQueryProps: {
      enabled: canViewDashboard,
    },
  });

  useEffect(() => {
    if (hasShownOnboardingModalRef.current) {
      return;
    }

    const createdHeatmapsParam = searchParams.get('heatmaps_created');

    if (createdHeatmapsParam === null) {
      return;
    }

    const nextSearchParams = new URLSearchParams(searchParams);
    nextSearchParams.delete('heatmaps_created');
    setSearchParams(nextSearchParams, { replace: true });

    const createdHeatmapsCount = Number.parseInt(createdHeatmapsParam ?? '', 10);

    if (Number.isNaN(createdHeatmapsCount) || createdHeatmapsCount <= 0) {
      return;
    }

    hasShownOnboardingModalRef.current = true;
    modal.show(<OnboardingHeatmapsCreatedModal count={createdHeatmapsCount} onHide={modal.close} />);
  }, [modal, searchParams, setSearchParams]);

  if (!canViewDashboard) {
    return <DashboardPaywall feature={FEATURES.SNAPSHOTS} reason={reason} paddingLeftRight="40px" />;
  }

  return (
    <DashboardPage>
      <SEO title={getFeatureName(FEATURES.SNAPSHOTS)} />
      <FilterProvider>
        <SelectionProvider>
          <SnapshotDashHeader />
          <SitePickerBar
            Picker={SourceSelector}
            sites={sites}
            loading={loadingSites}
            enableAllSites={true}
            addSnapshotOption={canCreate && canEditSites}
            addSiteOption={canEditSites}
            pageDisplayName={`${getFeatureName(FEATURES.SNAPSHOTS)} dashboard`}
          />

          <>
            <SnapshotAlerts />
            <InstallationBanner />
          </>

          <SnapshotActionProvider>
            <SnapshotDashboardContent sites={sites} />
          </SnapshotActionProvider>
        </SelectionProvider>
      </FilterProvider>
    </DashboardPage>
  );
}
